import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Phone, Contact, Users, Settings, AlertTriangle } from 'lucide-react';
import { SplitProvider } from '../components/SplitView';
import { useAccountBase, useCurrentAccount } from '../account/AccountContext';
import { AccountSwitcher } from '../account/AccountSwitcher';

/** Bottom tab bar, one entry per top-level area.
 *
 *  `path` values that have no page yet are still listed: the tab bar is the app's
 *  spine and a missing destination is a routing gap, not a reason to hide the
 *  door. Each renders a placeholder until its screen lands.
 *
 *  Every entry hangs off the current line (`/a/:accountId/…`), so switching lines
 *  keeps you on the same tab instead of dropping you at the call list. */
const TABS = [
  { path: '', icon: Phone, label: 'nav.calls' },
  { path: '/contacts', icon: Contact, label: 'nav.contacts' },
  { path: '/team', icon: Users, label: 'nav.team' },
  { path: '/settings', icon: Settings, label: 'nav.settings' },
] as const;

/** The customer's shell — mobile-first, because this is a phone app that happens
 *  to work on a desktop.
 *
 *  Deliberately NOT the admin shell with different colours: no left rail, no
 *  resizable split panel, no topbar breadcrumbs. Navigation is a bottom tab bar
 *  within thumb reach, and each screen owns its own header.
 *
 *  Both shells are the same installed PWA — one manifest, one service worker. The
 *  role picks which one mounts, so a platform admin who installs the app gets the
 *  back office and a customer gets this. */
export default function CustomerShell() {
  const { t } = useTranslation();
  const base = useAccountBase();
  const navigate = useNavigate();
  const account = useCurrentAccount();

  return (
    <div className="relative h-dvh flex flex-col bg-background overflow-hidden">
      {/* Setup prompt, never a block. A line with no number yet (`needsOnboarding`)
       *  or a number whose wizard was left unfinished (`setupUnfinished`) both land
       *  the owner in the app with a persistent banner into the wizard — the app is
       *  usable meanwhile, and number assignment is admin-only anyway, so bouncing
       *  the owner into a wizard they cannot finish was a dead end. Only OWNER/ADMIN
       *  ever see either flag true (the server reports both false for a delegate). */}
      {(account.needsOnboarding || account.setupUnfinished) && (
        <button
          type="button"
          onClick={() => navigate(`${base}/onboarding`)}
          className="flex shrink-0 items-center gap-2 border-b border-amber-500/30 bg-amber-500/10 px-4 py-2 text-left text-xs text-amber-700 dark:text-amber-400"
        >
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span className="flex-1 min-w-0">
            {t(account.needsOnboarding ? 'onboarding.banner.noNumber' : 'onboarding.banner.unfinished')}
          </span>
          <span className="shrink-0 font-medium underline">{t('onboarding.banner.finish')}</span>
        </button>
      )}

      {/* The one thing above the screen's own header, and it renders nothing at all
       *  for the ordinary customer — one line, and it is theirs. It appears when
       *  there is somewhere to switch to, or when the line is not yours; see
       *  AccountSwitcher. That keeps the rule below intact for everyone it was
       *  written for. */}
      <AccountSwitcher />

      {/* Screens own their header — a call list needs filter chips, settings needs
       *  a back arrow. A shared topbar would fight both.
       *
       *  SplitProvider owns the list/detail story. On a phone it is not a split at
       *  all — the detail replaces the list full-screen with a back button, which is
       *  how the customer app is meant to read. Above md it becomes the resizable
       *  two-pane the admin shell uses. */}
      <main className="flex-1 min-w-0 overflow-hidden">
        <SplitProvider>
          <Outlet />
        </SplitProvider>
      </main>

      {/* Floating pill nav — icons only, centred, hovering above content in a
       *  rounded card of its own. Positioned absolutely (out of flow) so pages
       *  scroll freely behind it, as if it sits on top of them. `pointer-events-none`
       *  on the bar lets taps on the empty gutters fall through to the content
       *  underneath; the pill re-enables them. Labels live on `aria-label`/`title`;
       *  four familiar icons carry it without text. */}
      <nav
        aria-label="Primary"
        className="pointer-events-none absolute inset-x-0 bottom-0 z-10 flex justify-center px-4 pt-2 pb-[calc(env(safe-area-inset-bottom)+0.5rem)]"
      >
        <ul className="pointer-events-auto flex items-center gap-1 rounded-full border border-border bg-card px-2 py-1.5 shadow-lg">
          {TABS.map(({ path, icon: Icon, label }) => (
            <li key={path}>
              {/* NavLink decides active itself rather than us comparing pathnames:
                  `end` on the account root keeps it from matching every tab below it,
                  which is the bug hand-rolled prefix matching kept reintroducing. */}
              <NavLink
                to={`${base}${path}`}
                end={path === ''}
                aria-label={t(label)}
                title={t(label)}
                className={({ isActive }) =>
                  [
                    'flex h-11 w-11 items-center justify-center rounded-full transition-colors',
                    isActive
                      ? 'bg-primary/15 text-primary'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                  ].join(' ')
                }
              >
                <Icon className="h-5 w-5 shrink-0" />
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}
